/** MI DOMINO - the app shell around the table: ⚙️ settings panel, page chrome
 *  (store close / settings toggle), the top-bar tabs, the language toggle, the
 *  chat (emoji picker + the compays' replies) and the phone bar / drawer. */
import { NAMES } from '../data/config';
import { openTienda, renderTienda, saveWallet, tpToggle } from '../economy/tienda';
import { applyScene, syncFeltSelect, syncSkinSelect, syncWoodSelect } from '../economy/wallet';
import { L, render, toggleLang } from '../game/engine';
import { clack, setSound, soundOn } from '../game/sounds';
import { NET, S, SETTINGS, WALLET, saveSettings, socket } from '../state';
import { renderDash } from './dashboard';
import { $ } from './dom';
import { addChat } from './feed';
import { LANG, t } from './i18n';

/** ⚙️ settings panel: felt / wood / scene / fichas selects (restored from
 *  storage), on-table hints and the sound toggle. Needs the settings pop DOM;
 *  the store-backed option lists are synced right after (see main.ts). */
export function initSettingsPanel(): void {
  /* ---------- customization ---------- */
  /* ⚙️ Table felt: built-in felts OR a bought flag/designer table (equipped
     through the wallet, same path as the store — the two used to be separate,
     so a bought table never showed up here). */
  $('felt-select').onchange = (e: any) => {
    const v = e.target.value;
    document.body.classList.remove('felt-wine', 'felt-ocean', 'felt-royal');
    if (v.startsWith('flag-') || v.startsWith('mesa-')) {
      try { localStorage.setItem('felt', ''); } catch (err) {}
      if (!WALLET.active.includes(v)) tpToggle(v); else syncFeltSelect();
      return;
    }
    WALLET.active = WALLET.active.filter(id => !id.startsWith('flag-') && !id.startsWith('mesa-'));
    saveWallet();   // → applyBling clears the inline felt paint
    if (v) document.body.classList.add(v);
    try { localStorage.setItem('felt', v); } catch (err) {}
    syncFeltSelect();
  };
  /* ⚙️ Table wood: classic OR a bought wood finish */
  const woodSel = $('wood-select');
  if (woodSel) woodSel.onchange = (e: any) => {
    const v = e.target.value;
    if (v.startsWith('skin-')) { if (!WALLET.active.includes(v)) tpToggle(v); else syncWoodSelect(); return; }
    WALLET.active = WALLET.active.filter(id => !id.startsWith('skin-'));
    saveWallet();
    syncWoodSelect();
  };
  /* 🌅 Scene: where you're playing (beach, malecón sunset, night block…) */
  const sceneSel = $('scene-select');
  if (sceneSel) sceneSel.onchange = (e: any) => applyScene(e.target.value);
  $('skin').onchange = (e: any) => {
    const v = e.target.value;
    document.body.classList.remove('skin-midnight', 'skin-gold', 'skin-coral');
    if (v.startsWith('fich-')) {
      /* a bought set: equip it through the wallet (same path as the store) */
      try { localStorage.setItem('skin', ''); } catch (err) {}
      if (!WALLET.active.includes(v)) tpToggle(v); else syncSkinSelect();
      return;
    }
    /* a built-in look: it replaces whatever store set was equipped */
    WALLET.active = WALLET.active.filter(id => !id.startsWith('fich-'));
    saveWallet();
    if (v) document.body.classList.add(v);
    try { localStorage.setItem('skin', v); } catch (err) {}
    syncSkinSelect();
  };
  try {
    const f = localStorage.getItem('felt'), sk = localStorage.getItem('skin');
    if (f) { $('felt-select').value = f; document.body.classList.add(f); }
    if (sk) { $('skin').value = sk; document.body.classList.add(sk); }
  } catch (err) {}
  /* (syncSkinSelect() runs from main.ts right after this, once the store sets are in STORE) */
  /* on-table assists (drop-spot hints + playable highlight) — toggle + persist */
  const hintsSel = $('hints-select');
  if (hintsSel) {
    hintsSel.value = SETTINGS.hints ? 'on' : 'off';
    hintsSel.onchange = (e: any) => { SETTINGS.hints = e.target.value === 'on'; saveSettings(); if (S && NET.active === false) render(); };
  }
  // interface size (Normal / Grande / Extra grande) — remembered on this device
  const uiSel = $('ui-size-select');
  const readSize = (): string => { try { return localStorage.getItem('mdUiSize') || 'normal'; } catch (e) { return 'normal'; } };
  const applyUiSize = (v: string) => {
    document.body.classList.remove('ui-grande', 'ui-xl');
    if (v === 'grande') document.body.classList.add('ui-grande');
    if (v === 'xl') document.body.classList.add('ui-xl');
  };
  applyUiSize(readSize());
  if (uiSel) {
    (uiSel as any).value = readSize();
    uiSel.onchange = (e: any) => {
      const v = e.target.value;
      try { localStorage.setItem('mdUiSize', v); } catch (err) {}
      applyUiSize(v);
    };
  }
  const soundSel = $('sound-select');
  if (soundSel) {
    soundSel.value = soundOn() ? 'on' : 'off';
    soundSel.onchange = (e: any) => { setSound(e.target.value === 'on'); if (e.target.value === 'on') clack(); };
  }
}

/** Store page close ✕ and the ⚙️ toggle. */
export function initPageChrome(): void {
  $('tp-close').onclick = () => {
    $('tienda-page').classList.remove('show');
  };
  /* dashboard button lives top-left on the arena */
  $('btn-settings').onclick = () => {
    $('settings-pop').classList.toggle('show');
  };
}

/* APP-SHELL tabs: 🎲 Mesa closes the dashboard/store pages; the active pill
   follows whichever page is showing (observed, so every open/close path —
   buttons, "VOLVER A LA MESA", the store's own close — stays in sync). */
export function initShellTabs(): void {
  const dash = $('dash-page'), store = $('tienda-page');
  const tabs = { mesa: $('tab-mesa'), dash: $('btn-dash'), store: $('btn-store') };
  if (!tabs.mesa || !dash || !store) return;
  const lobby = $('lobby-bg');
  const update = () => {
    const d = dash.classList.contains('show'), s = store.classList.contains('show');
    tabs.mesa.classList.toggle('active', !d && !s);
    tabs.dash.classList.toggle('active', d && !s);
    tabs.store.classList.toggle('active', s);
    // body.table-ui = the TABLE is what's on screen (no page, no lobby, no
    // sign-in door). The phone's floating table controls (cam/mic, ☰ 🎉 🏆,
    // ⚙️, 📯) only exist in that state — they were hovering over the lobby
    // and the dashboard.
    const lobbyUp = !!(lobby && lobby.classList.contains('show'));
    const doorUp = !!$('enroll-ov');
    document.body.classList.toggle('table-ui', !d && !s && !lobbyUp && !doorUp);
  };
  tabs.mesa.onclick = () => { dash.classList.remove('show'); store.classList.remove('show'); update(); };
  const mo = new MutationObserver(update);
  mo.observe(dash, { attributes: true, attributeFilter: ['class'] });
  mo.observe(store, { attributes: true, attributeFilter: ['class'] });
  if (lobby) mo.observe(lobby, { attributes: true, attributeFilter: ['class'] });
  mo.observe(document.body, { childList: true });   // the sign-in door is added/removed as an overlay
  update();
}

// Toggle language AND live-refresh whatever full-screen view is open, so the
// dashboard/shop re-translate immediately instead of only on next open.
function switchLang() {
  toggleLang();
  try {
    if ($('dash-page')?.classList.contains('show')) renderDash();
    if ($('tienda-page')?.classList.contains('show')) renderTienda();
    if (S && (NET.active === false || !NET.active)) render();
  } catch (e) { /* view not open */ }
}

/** 🛒 Tienda tab + the two language buttons. */
export function initLangAndStore(): void {
  $('btn-store').onclick = () => openTienda(undefined);   // shell: Tienda tab = the store page
  $('btn-lang').onclick = switchLang;
  const dl = $('dash-lang'); if (dl) dl.onclick = switchLang;
}

/* 😀 emoji picker for the chat — a grid of table-talk emoji; tap inserts at the
   caret and keeps the input focused, so you can chain them. Closes on send,
   Escape or a click outside. (Emoji already travel fine end-to-end; this is
   for desktops without an emoji keyboard and for one-tap reactions.) */
export function initEmojiPicker(): void {
  const btn = $('chat-emoji'), pop = $('emoji-pop'), inp = $('chat-input');
  if (!btn || !pop || !inp) return;
  const EMOJI = ['😂', '🤣', '😎', '🔥', '👏', '💪', '🎉', '🏆', '🎯', '💥', '🔒', '🪙', '👑', '🤝', '❤️', '😤', '😱', '🤯', '💀', '🙈', '👀', '🙏', '🍻', '☕', '🎺', '📯', '🫡', '🤌', '😴', '🥶', '🫠', '🤡'];
  pop.innerHTML = EMOJI.map(e => `<button type="button" class="emo" data-e="${e}">${e}</button>`).join('');
  const close = () => { pop.style.display = 'none'; };
  btn.onclick = () => { pop.style.display = pop.style.display === 'none' ? 'grid' : 'none'; if (pop.style.display === 'grid') inp.focus(); };
  pop.onclick = (ev: any) => {
    const b = ev.target.closest('.emo');
    if (!b) return;
    const e = b.dataset.e;
    const s = inp.selectionStart ?? inp.value.length, en = inp.selectionEnd ?? s;
    const next = inp.value.slice(0, s) + e + inp.value.slice(en);
    if (next.length > 120) return;
    inp.value = next;
    inp.focus();
    inp.setSelectionRange(s + e.length, s + e.length);
  };
  document.addEventListener('click', (ev: any) => { if (!pop.contains(ev.target) && ev.target !== btn) close(); });
  document.addEventListener('keydown', (ev: any) => { if (ev.key === 'Escape') close(); });
  $('chat-form').addEventListener('submit', close);
}

/* ⚡ one-tap table talk: the phrases every Puerto Rican table shouts. Sends
   straight away (online → the socket, solo → the compays answer). In the
   player's language, so an English player isn't stuck typing Spanish. */
const QUICK_PHRASES: [string, string][] = [
  ['¡Wepa! 🎉', 'Wepa! 🎉'],
  ['¡Ay bendito! 😩', 'Ay bendito! 😩'],
  ['¡Dale, compay! 🔥', 'Go, compay! 🔥'],
  ['¡Buena jugada! 👏', 'Nice play! 👏'],
  ['¡Tranque a la vista! 🔒', 'Tranque coming! 🔒'],
  ['¡Capicú pa\' ti! 🎯', 'Capicú for you! 🎯'],
  ['¡Qué chavienda! 😤', 'What a bummer! 😤'],
  ['Jajaja 😂', 'Hahaha 😂'],
  ['¡Piensa rápido, que la mesa espera! ⏳', 'Think fast, the table\'s waiting! ⏳'],
  ['GG · ¡Buena mesa! 🤝', 'GG · good game! 🤝'],
  ['¡Revancha! 🔁', 'Rematch! 🔁'],
];
export function initQuickPhrases(): void {
  const btn = $('chat-quick'), pop = $('quick-pop');
  if (!btn || !pop) return;
  const fill = () => { pop.innerHTML = QUICK_PHRASES.map((p, i) => `<button type="button" class="qp" data-i="${i}">${LANG === 'en' ? p[1] : p[0]}</button>`).join(''); };
  const close = () => { pop.style.display = 'none'; };
  btn.onclick = () => {
    const open = pop.style.display === 'none';
    $('emoji-pop').style.display = 'none';
    if (open) fill();
    pop.style.display = open ? 'grid' : 'none';
  };
  pop.onclick = (ev: any) => {
    const b = ev.target.closest('.qp');
    if (!b) return;
    const p = QUICK_PHRASES[+b.dataset.i];
    sendChat(LANG === 'en' ? p[1] : p[0]);
    close();
  };
  document.addEventListener('click', (ev: any) => { if (!pop.contains(ev.target) && ev.target !== btn) close(); });
  document.addEventListener('keydown', (ev: any) => { if (ev.key === 'Escape') close(); });
}

/** One chat line out: online → the socket; offline → the compays talk back. */
function sendChat(msg: string): void {
  if (NET.active && socket) {
    socket.emit('chat', { msg });
    return;
  }
  addChat(t('you'), msg, true);
  compayReply();
}

/** Chat form: online → the socket; offline → the compays talk back. */
export function initChat(): void {
  $('chat-form').addEventListener('submit', (ev: any) => {
    ev.preventDefault();
    const inp = $('chat-input');
    const msg = inp.value.trim();
    if (!msg) return;
    inp.value = '';
    sendChat(msg);
  });
}

function compayReply(): void {
    /* the compays talk back */
    if (Math.random() < 0.85) {
      const lines = [
        L('¡Dale compay! 😄', 'Go for it, compay! 😄'), L('¡Menos hablar y más jugar! 🎲', 'Less talking, more playing! 🎲'), L('Jajaja ¡eso dice tu boca!', 'Hahaha that\'s what your mouth says!'),
        L('¡Concéntrate, que la mesa está caliente! 🔥', 'Focus, the table is hot! 🔥'), L('¡Cuida tus fichas! 👀', 'Watch your dominos! 👀'),
        L('¡Tranquilo, tigre! 😎', 'Easy, tiger! 😎'), L('A esta mesa se viene a GANAR.', 'You come to this table to WIN.'), L('¿Y ese miedo, compay? 😂', 'Why so scared, compay? 😂'),
      ];
      setTimeout(() => {
        addChat(NAMES[1 + Math.floor(Math.random() * 3)], lines[Math.floor(Math.random() * lines.length)], false);
      }, 700 + Math.random() * 1400);
    }
}

/* ============================================================
   MOBILE SHELL — drawer, galería & score toggles, rotate-safe
   ============================================================ */
export function initMobileShell(): void {
  const bar = document.createElement('div');
  bar.id = 'mob-bar';
  bar.innerHTML = `
    <button id="mb-menu" title="${L('Menú', 'Menu')}">☰</button>
    <button id="mb-gal" title="${L('La Galería', 'The Gallery')}">🎉</button>
    <button id="mb-score" title="${L('Anotación', 'Score')}">🏆</button>`;
  document.body.appendChild(bar);
  const T = (id: string, cls: string, offCls: string[]) => {
    $(id).onclick = () => {
      for (const c of offCls) document.body.classList.remove(c);
      document.body.classList.toggle(cls);
    };
  };
  T('mb-menu', 'side-open', ['gal-open']);
  T('mb-gal', 'gal-open', ['side-open', 'score-open']);
  T('mb-score', 'score-open', ['gal-open']);
  /* close drawer button inside the sidebar */
  const sc = document.createElement('button');
  sc.id = 'side-close';
  sc.textContent = '✕';
  sc.onclick = () => document.body.classList.remove('side-open');
  $('sidebar').appendChild(sc);
  /* tapping the arena closes any open overlay */
  $('arena').addEventListener('click', () => {
    document.body.classList.remove('side-open');
  }, true);
  /* re-lay the snake when the phone rotates or resizes */
  let rzT: any = null;
  window.addEventListener('resize', () => {
    clearTimeout(rzT);
    rzT = setTimeout(() => {
      try { if (typeof S !== 'undefined' && S && S.board && S.board.length && typeof render === 'function') render(); } catch (e) {}
    }, 250);
  });
}
